/**
 * FlowGrid Component
 *
 * Renders the Knods title + toolbar + grid (or list) + skeletons + empty state.
 * Owns no state — purely driven by props from Dashboard. Extracted from the
 * 590-line Dashboard.tsx as a no-behavior-change refactor (Phase 0 of the
 * Projects rollout) so the Projects feature has a clean component to insert
 * into without ballooning Dashboard further.
 */

import React from 'react';
import type { SavedFlow } from '../../types/flow';
import { FlowCard, SkeletonFlowCard } from './index';

import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

const IrisAvatar: React.FC = () => (
  <img src="/icons/iris-butterfly.svg" alt="Iris" className="iris-butterfly-svg" />
);

export interface FlowGridProps {
  // Display state
  flows: SavedFlow[];
  loading: boolean;
  showSkeletons: boolean;
  isMobile: boolean;

  // Filter / sort / view state
  viewMode: 'grid' | 'list';
  searchQuery: string;
  showArchived: boolean;
  sortBy: 'created' | 'updated';
  sortOrder: 'asc' | 'desc';

  // Setters
  onSearchChange: (q: string) => void;
  onViewModeChange: (m: 'grid' | 'list') => void;
  onShowArchivedToggle: () => void;
  onSortByChange: (b: 'created' | 'updated') => void;
  onSortOrderToggle: () => void;

  // Card actions
  onOpenKnod: (flow: SavedFlow) => void;
  onCloneKnod: (flow: SavedFlow) => void;
  onRenameKnod: (flow: SavedFlow) => void;
  onArchiveKnod: (flow: SavedFlow) => void;
  onDeleteKnod: (flow: SavedFlow) => void;
  /** Optional — only wired when Projects v1 is enabled. */
  onMoveKnodToProject?: (flow: SavedFlow) => void;

  // Multi-select / bulk move — opt-in via Projects v1.
  selectionMode?: boolean;
  selectedFlowIds?: Set<string>;
  onEnterSelection?: () => void;
  onExitSelection?: () => void;
  onToggleSelect?: (flow: SavedFlow) => void;
  onBulkMove?: () => void;
  /**
   * When true, all visible flows are also currently selected. Lets the
   * "Select all" toggle flip between select-all and clear.
   */
  allVisibleSelected?: boolean;
  onToggleSelectAll?: () => void;

  // Empty state CTA
  onStartTutorial: () => void;
}

export const FlowGrid: React.FC<FlowGridProps> = ({
  flows,
  loading,
  showSkeletons,
  isMobile,
  viewMode,
  searchQuery,
  showArchived,
  sortBy,
  sortOrder,
  onSearchChange,
  onViewModeChange,
  onShowArchivedToggle,
  onSortByChange,
  onSortOrderToggle,
  onOpenKnod,
  onCloneKnod,
  onRenameKnod,
  onArchiveKnod,
  onDeleteKnod,
  onMoveKnodToProject,
  selectionMode = false,
  selectedFlowIds,
  onEnterSelection,
  onExitSelection,
  onToggleSelect,
  onBulkMove,
  allVisibleSelected = false,
  onToggleSelectAll,
  onStartTutorial,
}) => {
  const selectionAvailable = !!onEnterSelection;
  const selectedCount = selectedFlowIds?.size ?? 0;
  return (
    <section className="dashboard-flows-section">
      <h1 className="dashboard-flows-title">{showArchived ? 'Archived' : 'My Knods'}</h1>

      {/* Toolbar */}
      <div className="dashboard-toolbar">
        {/* Sort Controls */}
        <div className="dashboard-sort-controls">
          <select
            className="dashboard-sort-select"
            value={sortBy}
            onChange={(e) => onSortByChange(e.target.value as 'created' | 'updated')}
            aria-label="Sort by"
          >
            <option value="created">Created</option>
            <option value="updated">Edited</option>
          </select>
          <button
            className={`sort-order-btn`}
            onClick={onSortOrderToggle}
            aria-label={sortOrder === 'desc' ? 'Sort descending' : 'Sort ascending'}
            title={sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
          >
            {sortOrder === 'desc' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
          </button>
        </div>

        {!isMobile && (
          <div className="dashboard-view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => onViewModeChange('list')}
              aria-label="List view"
            >
              <ViewListIcon />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => onViewModeChange('grid')}
              aria-label="Grid view"
            >
              <ViewModuleIcon />
            </button>
          </div>
        )}

        {/* Archive Toggle */}
        <button
          className={`archive-toggle-btn ${showArchived ? 'active' : ''}`}
          onClick={onShowArchivedToggle}
          aria-label={showArchived ? 'Show active flows' : 'Show archived flows'}
          title={showArchived ? 'Showing archived' : 'Show archived'}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="21 8 21 21 3 21 3 8" />
            <rect x="1" y="3" width="22" height="5" />
            <line x1="10" y1="12" x2="14" y2="12" />
          </svg>
        </button>

        {/* Select toggle — only when projects are enabled. The bulk action
            bar below this toolbar takes over once selection mode is on. */}
        {selectionAvailable && !selectionMode && (
          <button
            type="button"
            onClick={onEnterSelection}
            className="archive-toggle-btn"
            title="Select multiple"
            aria-label="Select multiple"
            style={{ fontSize: 12, fontWeight: 600, padding: '0 10px' }}
          >
            Select
          </button>
        )}

        <div className="dashboard-search">
          <input
            type="text"
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="dashboard-search-input"
          />
          <SearchIcon className="dashboard-search-icon" />
        </div>
      </div>

      {/* Bulk action bar — visible only in selection mode. Renders above
          the grid so it stays in flow on every scroll position. */}
      {selectionMode && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '8px 14px',
            background: '#1a2a2e',
            border: '1px solid #2d4348',
            borderRadius: 8,
            margin: '0 0 12px',
          }}
        >
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 600 }}>
            {selectedCount} selected
          </span>
          {onToggleSelectAll && (
            <button
              type="button"
              onClick={onToggleSelectAll}
              style={{
                background: 'transparent',
                border: '1px solid #3a5a60',
                color: '#aaa',
                padding: '4px 10px',
                borderRadius: 4,
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              {allVisibleSelected ? 'Clear' : 'Select all'}
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button
            type="button"
            onClick={onBulkMove}
            disabled={selectedCount === 0}
            style={{
              background: selectedCount === 0 ? '#2a2a2a' : '#42C3D7',
              border: 'none',
              color: selectedCount === 0 ? '#666' : '#0a2730',
              padding: '6px 14px',
              borderRadius: 4,
              cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Move to project…
          </button>
          <button
            type="button"
            onClick={onExitSelection}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#aaa',
              padding: '6px 10px',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: 13,
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Flows Grid - Scrollable Container */}
      <div className="dashboard-flows-container">
        {/* Skeleton overlay - fades out when loading completes */}
        {showSkeletons && (
          <div className={`dashboard-flows-grid ${viewMode} skeleton-grid ${!loading ? 'fade-out' : ''}`}>
            {Array.from({ length: 24 }).map((_, index) => (
              <SkeletonFlowCard key={`skeleton-${index}`} viewMode={viewMode} index={index} />
            ))}
          </div>
        )}

        {/* Real content - shows after loading */}
        {!loading && (
          flows.length === 0 ? (
            <div className="dashboard-empty">
              {searchQuery ? (
                <p>No knods match your search.</p>
              ) : showArchived ? (
                <p>No archived knods.</p>
              ) : (
                <div className="dashboard-empty-iris">
                  <div className="dashboard-iris-scene">
                    <div className="dashboard-iris-npc">
                      <span className="dashboard-iris-nametag">Iris</span>
                      <div className="dashboard-iris-avatar">
                        <IrisAvatar />
                      </div>
                    </div>
                    <div className="dashboard-iris-bubble">
                      <p>Hey there! Ready to create something amazing?</p>
                    </div>
                  </div>
                  <button className="dashboard-empty-cta" onClick={onStartTutorial}>
                    Let's build your first Knod ✦
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className={`dashboard-flows-grid ${viewMode}`}>
              {flows.map((flow, index) => (
                <FlowCard
                  key={flow.metadata.id}
                  flow={flow}
                  onClick={() => onOpenKnod(flow)}
                  onClone={() => onCloneKnod(flow)}
                  onRename={() => onRenameKnod(flow)}
                  onArchive={() => onArchiveKnod(flow)}
                  onDelete={() => onDeleteKnod(flow)}
                  onMoveToProject={onMoveKnodToProject ? () => onMoveKnodToProject(flow) : undefined}
                  selectionMode={selectionMode}
                  selected={selectedFlowIds?.has(flow.metadata.id) ?? false}
                  onToggleSelect={onToggleSelect ? () => onToggleSelect(flow) : undefined}
                  viewMode={viewMode}
                  animationIndex={index}
                />
              ))}
            </div>
          )
        )}
      </div>
    </section>
  );
};

export default FlowGrid;
